export type InquiryStatus =
  | "new"
  | "contacted"
  | "quoted"
  | "deposit_paid"
  | "confirmed"
  | "completed"
  | "lost"
  | "cancelled";

export type EventType =
  | "corporate"
  | "birthday"
  | "bachelor_bachelorette"
  | "team_building"
  | "other";

export type Inquiry = {
  id: string;
  status: InquiryStatus;
  source: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  group_size: number;
  preferred_dates: Array<{ date: string; time?: string }>;
  event_type: EventType;
  duration_hours: number;
  food_interest: boolean;
  dietary_notes: string | null;
  budget_range: string | null;
  wants_callback: boolean;
  callback_number: string | null;
  callback_called?: boolean; // HQ migration 0037
  special_requests: string | null;
  checkfront_booking_code: string | null;
  checkfront_booking_url: string | null;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
  quoted_at: string | null;
  deposit_paid_at: string | null;
  confirmed_at: string | null;
  last_read_at: string | null;
  // HQ migration 0036: the Reservation System link (tribal-axe#10).
  reservation_id?: string | null;
  reservation_code?: string | null;
  reservation_quote_version?: number;
  reservation_quote?: { id: string; version: number; totalCents: number; depositCents: number } | null;
  reservation_status?: Record<string, unknown> | null;
  reservation_synced_at?: string | null;
};

export type Communication = {
  id: string;
  inquiry_id: string;
  direction: "inbound" | "outbound";
  channel: "email" | "sms" | "phone" | "note";
  from_address: string | null;
  to_address: string | null;
  subject: string | null;
  body: string | null;
  message_id: string | null;
  twilio_sid: string | null;
  sent_at: string | null;
  created_at: string;
};

export type Reminder = {
  id: string;
  inquiry_id: string;
  reminder_type: string;
  scheduled_for: string;
  sent_at: string | null;
  is_enabled: boolean;
  channel: string;
  checkfront_condition: Record<string, unknown> | null;
  created_at: string;
};

export type CallBookingStatus = "scheduled" | "completed" | "cancelled" | "no_show";

// A 15-minute callback booked from the public events widget. `inquiry_id` is
// nullable because the widget's booking flow does not yet link the call back to
// the inquiry that produced it.
export type CallBooking = {
  id: string;
  inquiry_id: string | null;
  guest_name: string;
  guest_email: string;
  guest_phone: string | null;
  scheduled_at: string;
  google_event_id: string | null;
  status: CallBookingStatus;
  notes: string | null;
  cancelled_at: string | null;
  created_at: string;
};

// A weekly window when callbacks can be booked. Times come back from Postgres
// as "HH:MM:SS".
export type AvailabilityBlock = {
  id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
  created_at: string;
};

export type DateOverride = {
  id: string;
  date: string;
  override_type: "unavailable" | "available";
  start_time: string | null;
  end_time: string | null;
  note: string | null;
  created_at: string;
};

export type Quote = {
  id: string;
  inquiry_id: string;
  checkfront_products: unknown[];
  subtotal: number;
  tax: number;
  total: number;
  deposit_amount: number;
  booking_url: string | null;
  sent_at: string | null;
  expires_at: string | null;
  created_at: string;
};

// ReservationSystem #484 (owner, 2026-10-17): labels follow the Reservation System's
// wording; the status values are unchanged. confirmed is paid in full and shares
// the Deposit Paid - Confirmed pipeline column; lost is a declined Proposal.
export const STATUS_LABELS: Record<InquiryStatus, string> = {
  new: "New",
  contacted: "Contacted",
  quoted: "Proposal Sent",
  deposit_paid: "Deposit Paid - Confirmed",
  confirmed: "Paid in Full",
  completed: "Completed",
  lost: "Declined",
  cancelled: "Cancelled",
};

export const STATUS_COLORS: Record<InquiryStatus, string> = {
  new: "bg-blue-500",
  contacted: "bg-yellow-500",
  quoted: "bg-purple-500",
  deposit_paid: "bg-orange-500",
  confirmed: "bg-green-500",
  completed: "bg-gray-500",
  lost: "bg-red-500",
  cancelled: "bg-gray-700",
};

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  corporate: "Corporate",
  birthday: "Birthday",
  bachelor_bachelorette: "Bachelor/Bachelorette",
  team_building: "Team Building",
  other: "Other",
};

/** When and how big the event is: the Reservation's own schedule once HQ has
 * synced it (ReservationSystem #469, edited there), else the inquiry's request. */
export function eventSchedule(i: Inquiry) {
  const s = i.reservation_status as { localDate?: unknown; startMinute?: unknown; durationMinutes?: unknown; partySize?: unknown } | null | undefined;
  if (typeof s?.localDate === "string" && typeof s.startMinute === "number" && typeof s.durationMinutes === "number" && typeof s.partySize === "number")
    return { fromReservation: true, date: s.localDate as string | undefined, groupSize: s.partySize, hours: s.durationMinutes / 60,
      time: `${String(Math.floor(s.startMinute / 60)).padStart(2, "0")}:${String(s.startMinute % 60).padStart(2, "0")}` as string | undefined };
  return { fromReservation: false, date: i.preferred_dates[0]?.date, time: i.preferred_dates[0]?.time, hours: i.duration_hours, groupSize: i.group_size };
}

/** "14:30" → "2:30pm"; anything else is shown as typed. */
export function formatTime12h(time: string): string {
  const [h, m] = time.split(":").map(Number);
  if (isNaN(h)) return time;
  const period = h >= 12 ? "pm" : "am";
  const hour12 = h % 12 || 12;
  return m ? `${hour12}:${String(m).padStart(2, "0")}${period}` : `${hour12}${period}`;
}
